import type {ReactNode} from 'react';
import React from 'react';

import {Heading, Icon, Text} from '@albert-io/atomic';

import type {QuestionModelV3, QuestionSetModelV1, SubjectModelV2} from '@albert-io/models';

import {QuestionWrapper, HeadingBar} from 'client/components/QuestionTypes/shared/ContentBlocks';

import {RESTRICTED_QUESTION_LABEL} from './teacherRestriction.utils';

interface NavigationProps {
  disabled: boolean;
  handleNavigation: () => void;
}

interface Props {
  actions?: ReactNode;
  activeQuestion: QuestionModelV3;
  activeQuestionSet: QuestionSetModelV1;
  activeSubject: SubjectModelV2;
  headingActions: ReactNode;
  navigateBackProps: NavigationProps | null;
  navigateNextProps: NavigationProps | null;
}

// 520px (readable paragraph measure) is an intentional arbitrary value above the spacing-token scale.
const PANEL_CONTENT_MAX_WIDTH = 'max-w-[520px]';

const SHIELD_ICON_CLASS_NAME = 'text-xxl';

/**
 * Metadata (title, difficulty, navigation) stays visible because the backend
 * redacts only content (stem/answer/explanation) so builder and reporting keep
 * working. Copy stays neutral: a teacher can't read which schools restricted it.
 */
export default function BlockedForTeacherQuestion({
  activeQuestion,
  activeQuestionSet,
  activeSubject,
  actions,
  navigateBackProps,
  navigateNextProps,
  headingActions
}: Props) {
  return (
    <QuestionWrapper
      question={activeQuestion}
      questionSet={activeQuestionSet}
      subject={activeSubject}
      heading={<HeadingBar question={undefined} questionSet={undefined} showDifficulty />}
      modifyTitle={() => RESTRICTED_QUESTION_LABEL}
      navigateBackProps={navigateBackProps}
      navigateNextProps={navigateNextProps}
      headingActions={headingActions}
    >
      <div className={`mx-auto flex flex-col gap-x4 p-x6 ${PANEL_CONTENT_MAX_WIDTH}`}>
        <div className='flex items-center gap-x1'>
          <Icon
            icon='shield'
            iconStyle='solid'
            color='warning'
            size='inherit'
            className={SHIELD_ICON_CLASS_NAME}
          />
          {/* h2 (not h3): title above is h1, so h3 would skip a level and fail heading-order a11y. */}
          <Heading as='h2' size='m' className='m-x0!'>
            Access restricted by your admin
          </Heading>
        </div>
        <div className='flex flex-col gap-x2-half'>
          <div className='flex gap-x1-half'>
            <Icon icon='eye-slash' iconStyle='light' color='primary' size='standalone' className='mt-x0-half' />
            <div className='flex flex-col gap-x0-half'>
              <Text bold>For you</Text>
              <Text color='secondary'>
                The question still appears in your assignments and reports, but you can&apos;t see
                the content.
              </Text>
            </div>
          </div>
          <div className='flex gap-x1-half'>
            <Icon icon='graduation-cap' iconStyle='light' color='primary' size='standalone' className='mt-x0-half' />
            <div className='flex flex-col gap-x0-half'>
              <Text bold>For your students</Text>
              <Text color='secondary'>
                The question still appears in their assignments, but they can&apos;t see the content
                or submit answers unless an admin authorizes the assignment.
              </Text>
            </div>
          </div>
        </div>
      </div>
      {actions || null}
    </QuestionWrapper>
  );
}
